import React, { useState, useRef, useCallback, useMemo } from "react";
import Grid from '@mui/material/Grid2';
import LoadingBox from "@components/input/LoadingBox";
import PageLinks from "@components/Navigation/PageLinks";
import ActiveObjectToolbar from "./ActiveObjectToolbar";
import PreviewModal from "./PreviewModal";
import RepeatedLinesBanner from "./RepeatedLinesBanner";
import CheckBox from "@components/input/CheckBox";
import { SBSection } from "@dndModels";
import {
  PRESIGN_URL, PARSE_URL, BUCKET, PAGES_PER_BATCH,
  ExtractedObject, ParsedPage, ParseResult, Status,
  emptyObject, stripLinesFromPages, autoGenerateSections,
  applyBoldHeader, 
} from "./PdfImporter.types";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface PdfImporterProps {
  onExport?: (sections: SBSection[]) => void;
}

export default function PdfImporter({ onExport }: PdfImporterProps) {
  const [s3Key, setS3Key] = useState("pdf-imports/SRD-OGL_V5.1.pdf");
  const [startPage, setStartPage] = useState("114");
  const [endPage, setEndPage] = useState("123");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<ParsedPage[]>([]);
  const [selPageNum, setSelPageNum] = useState(0);
  // const [page, setPage] = useState<ParsedPage | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);

  const [activeObject, setActiveObject] = useState<ExtractedObject | null>(null);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [appendMode, setAppendMode] = useState(false);
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [extractedObjects, setExtractedObjects] = useState<ExtractedObject[]>([]);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [previewObject, setPreviewObject] = useState<ExtractedObject | null>(null);
  const [removedLines, setRemovedLines] = useState<Set<string>>(new Set());
  const [ignoredLines, setIgnoredLines] = useState<Set<string>>(new Set());
  const [mergeSelectedIds, setMergeSelectedIds] = useState<Set<string>>(new Set());

  const effectivePages = useMemo(
    () => removedLines.size > 0 ? stripLinesFromPages(pages, removedLines) : pages,
    [pages, removedLines]
  );

  // const setSelPage = (num: number) => {
  //   setSelPageNum(num);
  //   setPage(effectivePages[num]);
  // }

  // ─── Parse / Upload ──────────────────────────────────────────────────────

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setError(null); setStatus("uploading");
    try {
      const presignRes = await fetch(PRESIGN_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ filename: file.name }) });
      if (!presignRes.ok) throw new Error("Failed to get upload URL");
      const { url, key } = await presignRes.json();
      const uploadRes = await fetch(url, { method: "PUT", headers: { "Content-Type": "application/pdf" }, body: file });
      if (!uploadRes.ok) throw new Error("S3 upload failed");
      setS3Key(key); setStatus("idle");
    } catch (err: any) { setError(err.message); setStatus("error"); }
  };

  const callParse = async (key: string, start: number, end: number): Promise<ParseResult> => {
    const res = await fetch(PARSE_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ bucket: BUCKET, key, start_page: start, end_page: end }) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Parse failed");
    return data;
  };

  const handleParseRange = async () => {
    if (!s3Key.trim() || !startPage) return;
    setError(null); setPages([]); setStatus("parsing"); setProgress(0); 
    // setPage(undefined);
    try {
      const start = parseInt(startPage), end = endPage ? parseInt(endPage) : start;
      const total = end - start + 1;
      const allPages: ParsedPage[] = [];
      for (let p = start; p <= end; p += PAGES_PER_BATCH) {
        const batchEnd = Math.min(p + PAGES_PER_BATCH - 1, end);
        setProgressMessage(`Parsing pages ${p}–${batchEnd} of ${end}…`);
        const result = await callParse(s3Key, p, batchEnd);
        allPages.push(...result.pages);
        console.log(allPages);
        setPages([...allPages]);
        setProgress(Math.round((allPages.length / total) * 100));
      }
      setSelPageNum(0);
      // setPage(allPages[0]); 
      setStatus("done");
    } catch (err: any) { setError(err.message); setStatus("error"); }
  };

  const handleParseAll = async () => {
    if (!s3Key.trim()) return;
    setError(null); setPages([]); setProgress(0); 
    // setPage(undefined); 
    abortRef.current = false;
    try {
      setStatus("counting"); setProgressMessage("Getting page count…");
      const countRes = await fetch(PARSE_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ bucket: BUCKET, key: s3Key.trim(), page_count_only: true }) });
      const countData = await countRes.json();
      if (!countRes.ok) throw new Error(countData.error || "Failed to get page count");
      const totalPages: number = countData.total_pages;
      setStatus("parsing");
      const allPages: ParsedPage[] = [];
      for (let p = 1; p <= totalPages; p += PAGES_PER_BATCH) {
        if (abortRef.current) break;
        const batchEnd = Math.min(p + PAGES_PER_BATCH - 1, totalPages);
        setProgressMessage(`Parsing pages ${p}–${batchEnd} of ${totalPages}…`);
        const result = await callParse(s3Key, p, batchEnd);
        allPages.push(...result.pages);
        setPages([...allPages]);
        setProgress(Math.round((allPages.length / totalPages) * 100));
      }
      setSelPageNum(0);
      // setPage(allPages[0]); 
      setStatus(abortRef.current ? "idle" : "done");
    } catch (err: any) { setError(err.message); setStatus("error"); }
  };

  // ─── Object Management ───────────────────────────────────────────────────

  const saveActiveObject = useCallback((obj: ExtractedObject) => {
    if (Object.keys(obj.fields).length === 0) return;
    setExtractedObjects(prev => {
      const idx = prev.findIndex(o => o.id === obj.id);
      return idx >= 0 ? prev.map(o => o.id === obj.id ? obj : o) : [...prev, obj];
    });
  }, []);

  // Reconciles activeObject edits into a list, same logic as allSavedObjects
  const reconciled = useCallback((base: ExtractedObject[]): ExtractedObject[] => {
    if (!activeObject) return base;
    if (base.some(o => o.id === activeObject.id)) return base.map(o => o.id === activeObject.id ? activeObject : o);
    return Object.keys(activeObject.fields).length > 0 ? [...base, activeObject] : base;
  }, [activeObject]);

  const handleNewObject = useCallback(() => {
    if (activeObject) saveActiveObject(activeObject);
    const next = emptyObject();
    setActiveObject(next);
    setSelectedObjectId(next.id);
    setActiveField(null);
    setPendingText(null);
    setAppendMode(false);
  }, [activeObject, saveActiveObject]);

  const handleTextMouseUp = useCallback(() => {
    if (!activeObject) return;
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (!text) return;
    setPendingText(text);
  }, [activeObject]);

  const handleFieldButton = useCallback((field: string) => {
    if (!activeObject) return;
    if (pendingText) {
      const existing = activeObject.fields[field];
      const newValue = appendMode && existing ? `${existing} ${pendingText}` : pendingText;
      const updated = { ...activeObject, fields: { ...activeObject.fields, [field]: newValue } };
      setActiveObject(updated);
      setPendingText(null);
      window.getSelection()?.removeAllRanges();
    }
    setActiveField(field);
  }, [activeObject, pendingText, appendMode]);

  const handleSelectObject = (obj: ExtractedObject) => {
    if (activeObject) saveActiveObject(activeObject);
    setActiveObject({ ...obj });
    setSelectedObjectId(obj.id);
    setActiveField(null);
    setPendingText(null);
    setAppendMode(false);
  };

  const handleDeleteObject = (id: string) => {
    setExtractedObjects(prev => prev.filter(o => o.id !== id));
    setMergeSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    if (selectedObjectId === id) {
      setActiveObject(null); setSelectedObjectId(null);
      setActiveField(null); setPendingText(null);
    }
  };

  const handleToggleMergeSelect = useCallback((id: string, orderedIds: string[]) => {
    setMergeSelectedIds(prev => {
      const isChecking = !prev.has(id);
      if (isChecking && prev.size > 0) {
        const clickedIdx = orderedIds.indexOf(id);
        let nearest = -1;
        let nearestDist = Infinity;
        for (const selectedId of Array.from(prev)) {
          const idx = orderedIds.indexOf(selectedId);
          if (idx !== -1 && Math.abs(idx - clickedIdx) < nearestDist) {
            nearest = idx;
            nearestDist = Math.abs(idx - clickedIdx);
          }
        }
        if (nearest !== -1) {
          const lo = Math.min(nearest, clickedIdx);
          const hi = Math.max(nearest, clickedIdx);
          const next = new Set(prev);
          for (let i = lo; i <= hi; i++) next.add(orderedIds[i]);
          return next;
        }
      }
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleMergeSelected = useCallback(() => {
    if (mergeSelectedIds.size < 2) return;
    setExtractedObjects(prev => {
      const working = reconciled(prev);
      // Find the selected objects in list order; the first becomes the target
      const selected = working.filter(o => mergeSelectedIds.has(o.id));
      if (selected.length < 2) return prev;
      const [target, ...rest] = selected;
      // debugger;
      const mergedContent = [
        target.fields["content"] ?? "",
        ...rest.flatMap(o => [o.fields["name"] ? `\n${applyBoldHeader(o.fields["name"])}` : "\n", o.fields["content"] ?? ""]),
      ].filter(Boolean).join("\n");
      const updatedTarget = { ...target, fields: { ...target.fields, content: mergedContent } };
      const restIds = new Set(rest.map(o => o.id));
      const next = working
        .filter(o => !restIds.has(o.id))
        .map(o => o.id === target.id ? updatedTarget : o);
      // Sync active object if it was involved
      if (mergeSelectedIds.has(activeObject?.id ?? "")) {
        setActiveObject(restIds.has(activeObject!.id) ? updatedTarget : updatedTarget);
        setSelectedObjectId(updatedTarget.id);
      }
      return next;
    });
    setMergeSelectedIds(new Set());
  }, [mergeSelectedIds, activeObject, reconciled]);

  // ─── Auto Sections / Export ──────────────────────────────────────────────

  const handleAutoSections = useCallback(() => {
    if (activeObject) saveActiveObject(activeObject);
    const generated = autoGenerateSections(effectivePages);
    if (generated.length === 0) return;
    setExtractedObjects(prev => [...prev, ...generated]);
    setActiveObject({ ...generated[0] });
    setSelectedObjectId(generated[0].id);
    setActiveField(null);
    setPendingText(null);
    setAppendMode(false);
  }, [activeObject, effectivePages, saveActiveObject]);

  const handleExport = useCallback(() => {
    if (!onExport) return;
    if (activeObject) saveActiveObject(activeObject);
    const saved = reconciled(extractedObjects);
    const sectionMap = new Map<string, SBSection>();
    for (const obj of saved) {
      sectionMap.set(obj.id, new SBSection({
        name: obj.fields["name"] ?? "",
        content: obj.fields["content"] ?? "",
      }));
    }
    const topLevel: SBSection[] = [];
    for (const obj of saved) {
      const section = sectionMap.get(obj.id)!;
      const parentId = obj.fields["parent_id"];
      if (parentId && sectionMap.has(parentId)) {
        sectionMap.get(parentId)!.subsections.push(section);
      } else {
        topLevel.push(section);
      }
    }
    onExport(topLevel);
  }, [activeObject, extractedObjects, saveActiveObject, reconciled, onExport]);

  const handleIgnoreLine = useCallback((lines: string[]) => {
    setIgnoredLines(prev => new Set(Array.from(prev).concat(lines)));
  }, []);

  const handleUnignoreLine = useCallback((lines: string[]) => {
    setIgnoredLines(prev => {
      const next = new Set(prev);
      lines.forEach(l => next.delete(l));
      return next;
    });
  }, []);

  const handleAddRemovedLine = useCallback((lines: string[]) => {
    setRemovedLines(prev => new Set(Array.from(prev).concat(lines)));
  }, []);

  const handleRestoreLine = useCallback((lines: string[]) => {
    setRemovedLines(prev => {
      const next = new Set(prev);
      lines.forEach(l => next.delete(l));
      return next;
    });
  }, []);

  // ─── Derived State ───────────────────────────────────────────────────────

  const busy = status === "uploading" || status === "counting" || status === "parsing";
  const allSavedObjects = reconciled(extractedObjects);
  const orderedIds = allSavedObjects.map(o => o.id);
  const page = effectivePages[selPageNum];

  console.log(page);
  return (
    <Grid container spacing={2} direction="column">

      {/* Upload + Parse controls */}
      <Grid size={12}>
        <Grid container spacing={2}>
          <Grid>
            <fieldset>
              <legend>Upload PDF to S3</legend>
              <Grid container spacing={1} alignItems="center">
                <Grid><input ref={fileRef} type="file" accept="application/pdf" disabled={busy} /></Grid>
                <Grid><button onClick={handleUpload} disabled={busy}>{status === "uploading" ? "Uploading…" : "Upload"}</button></Grid>
              </Grid>
              {s3Key && <div style={{ marginTop: "0.4rem", fontSize: "0.8rem", color: "#555" }}>Uploaded: <code>{s3Key}</code></div>}
            </fieldset>
          </Grid>
          <Grid>
            <fieldset>
              <legend>Parse PDF</legend>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid>
                  <div style={{ fontSize: "0.8rem" }}>S3 Key</div>
                  <input value={s3Key} onChange={e => setS3Key(e.target.value)} placeholder="pdf-imports/SRD-OGL_V5.1.pdf" disabled={busy} style={{ width: "280px", padding: "0.3rem" }} />
                </Grid>
                <Grid>
                  <div style={{ fontSize: "0.8rem" }}>Start</div>
                  <input type="number" value={startPage} onChange={e => setStartPage(e.target.value)} placeholder="1" disabled={busy} style={{ width: "70px", padding: "0.3rem" }} />
                </Grid>
                <Grid>
                  <div style={{ fontSize: "0.8rem" }}>End</div>
                  <input type="number" value={endPage} onChange={e => setEndPage(e.target.value)} placeholder="10" disabled={busy} style={{ width: "70px", padding: "0.3rem" }} />
                </Grid>
                <Grid><button onClick={handleParseRange} disabled={busy || !s3Key.trim() || !startPage}>Parse Range</button></Grid>
                <Grid><button onClick={handleParseAll} disabled={busy || !s3Key.trim()}>Parse All</button></Grid>
                {busy && status !== "uploading" && <Grid><button onClick={() => abortRef.current = true} style={{ color: "red" }}>Cancel</button></Grid>}
              </Grid>
            </fieldset>
          </Grid>
        </Grid>
      </Grid>

      {(status === "counting" || status === "parsing") && (
        <Grid size={12}><LoadingBox message={progressMessage} percentage={progress} /></Grid>
      )}
      {error && <Grid size={12}><span style={{ color: "red" }}>Error: {error}</span></Grid>}

      {pages.length > 0 && (
        <Grid size={12}>
          <RepeatedLinesBanner
            pages={pages}
            ignoredLines={ignoredLines}
            removedLines={removedLines}
            onUnignore={handleIgnoreLine}
            onIgnore={handleUnignoreLine}
            onAdd={handleAddRemovedLine}
            onRemove={handleRestoreLine}
          />
        </Grid>
      )}

      {effectivePages.length > 0 && (
        <Grid size={12}>
          <Grid container spacing={2}>

            {/* Left: toolbar + content */}
            <Grid size="grow">
              <Grid container spacing={1} direction="column">

                {/* Top row: action buttons */}
                <Grid size={12}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid>
                      <button
                        onClick={handleAutoSections}
                        title="Scan all pages and generate sections from short header lines"
                        style={{ background: "#6a3db5", color: "white", border: "none", padding: "0.3rem 0.75rem", borderRadius: "4px", cursor: "pointer" }}
                      >
                        ⚡ Auto-Generate Sections
                      </button>
                    </Grid>
                    <Grid style={{ width: "1px", height: "1.4rem", background: "#ccc", margin: "0 0.25rem" }} />
                    <Grid>
                      <button
                        onClick={handleNewObject}
                        style={{ fontWeight: "bold", background: "#4a90d9", color: "white", border: "none", padding: "0.3rem 0.75rem", borderRadius: "4px", cursor: "pointer" }}
                      >
                        + New Section
                      </button>
                    </Grid>
                    {onExport && allSavedObjects.length > 0 && (
                      <>
                        <Grid style={{ width: "1px", height: "1.4rem", background: "#ccc", margin: "0 0.25rem" }} />
                        <Grid>
                          <button
                            onClick={handleExport}
                            title="Add all sections to the source book"
                            style={{ background: "#2a7a2a", color: "white", border: "none", padding: "0.3rem 0.75rem", borderRadius: "4px", cursor: "pointer" }}
                          >
                            ✓ Export to Source Book ({allSavedObjects.length})
                          </button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>

                {/* Active object toolbar */}
                {activeObject && (
                  <Grid size={12}>
                    <ActiveObjectToolbar
                      activeObject={activeObject}
                      appendMode={appendMode}
                      pendingText={pendingText}
                      activeField={activeField}
                      onAppendToggle={() => setAppendMode(a => !a)}
                      onFieldButton={handleFieldButton}
                    />
                  </Grid>
                )}

                {/* Page links */}
                <Grid size={12}>
                  <PageLinks selected={selPageNum} page_count={effectivePages.length} onChange={setSelPageNum} />
                </Grid>

                {/* Page text */}
                {page && (
                  <>
                    <Grid size={12}>
                      <div
                        onMouseUp={handleTextMouseUp}
                        style={{
                          border: "1px solid #ddd",
                          padding: "1rem",
                          borderRadius: "4px",
                          cursor: activeObject ? "text" : "default",
                        }}
                      >
                        <DisplayStringWithReplacements display_string={page.text || "(no text)"} />
                      </div>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Right: sidebar */}
            <Grid size={3}>
              <Grid container spacing={0} direction="column" style={{ border: "1px solid #ddd", borderRadius: "4px", overflow: "hidden" }}>
                <Grid size={12} style={{ padding: "0.5rem 0.75rem", background: "#eee", fontWeight: "bold", fontSize: "0.9rem" }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size="grow">Sections ({allSavedObjects.length})</Grid>
                    {mergeSelectedIds.size >= 2 && (
                      <Grid>
                        <button
                          onClick={handleMergeSelected}
                          title="Merge selected sections into the topmost selected one"
                          style={{ fontSize: "0.8rem", padding: "0.15rem 0.5rem", borderRadius: "4px", border: "none", background: "#e07800", color: "white", cursor: "pointer" }}
                        >
                          ⤴ Merge {mergeSelectedIds.size}
                        </button>
                      </Grid>
                    )}
                    {mergeSelectedIds.size > 0 && (
                      <Grid>
                        <button
                          onClick={() => setMergeSelectedIds(new Set())}
                          title="Clear selection"
                          style={{ fontSize: "0.8rem", padding: "0.15rem 0.4rem", borderRadius: "4px", border: "1px solid #aaa", background: "none", cursor: "pointer", color: "#666" }}
                        >
                          ✕
                        </button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {allSavedObjects.length === 0 && (
                  <Grid size={12} style={{ padding: "0.75rem", fontSize: "0.85rem", color: "#888" }}>
                    No sections yet. Click "+ New Section" to start.
                  </Grid>
                )}

                {allSavedObjects.map((obj) => {
                  const isMergeSelected = mergeSelectedIds.has(obj.id);
                  return (
                    <Grid
                      key={obj.id}
                      size={12}
                      onClick={() => handleSelectObject(obj)}
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderBottom: "1px solid #eee",
                        cursor: "pointer",
                        background: isMergeSelected
                          ? "#fff3e0"
                          : selectedObjectId === obj.id ? "#e8f0fb" : undefined,
                        borderLeft: isMergeSelected ? "3px solid #e07800" : "3px solid transparent",
                      }}
                    >
                      <Grid container spacing={0} alignItems="flex-start" justifyContent="space-between">
                        <Grid style={{ display: "flex", alignItems: "center", paddingTop: "2px" }}>
                          <span onClick={e => e.stopPropagation()}>
                            <CheckBox
                              value={isMergeSelected}
                              onChange={() => handleToggleMergeSelect(obj.id, orderedIds)}
                              tooltip={isMergeSelected ? "Deselect for merge" : "Select for merge"}
                            />
                          </span>
                        </Grid>
                        <Grid size="grow" style={{ minWidth: 0, paddingLeft: "0.4rem" }}>
                          <div style={{ fontSize: "0.9rem", fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {obj.fields["name"] || <span style={{ color: "#aaa", fontStyle: "italic" }}>Unnamed</span>}
                          </div>
                          {obj.fields["content"] && (
                            <div style={{ fontSize: "0.75rem", color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {obj.fields["content"]}
                            </div>
                          )}
                        </Grid>
                        <Grid>
                          <Grid container spacing={0} direction="column" alignItems="center">
                            <Grid>
                              <button
                                onClick={e => { e.stopPropagation(); setPreviewObject(obj); }}
                                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.8rem", color: "#4a90d9", padding: "0 0 2px" }}
                                title="Preview"
                              >👁</button>
                            </Grid>
                            <Grid>
                              <button
                                onClick={e => { e.stopPropagation(); handleDeleteObject(obj.id); }}
                                style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontSize: "1rem", padding: 0 }}
                                title="Delete"
                              >×</button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

          </Grid>
        </Grid>
      )}

      {previewObject && (
        <PreviewModal obj={previewObject} allObjects={allSavedObjects} onClose={() => setPreviewObject(null)} />
      )}
    </Grid>
  );
}
